import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';

/**
 * CPF.
 *
 * Value object porque o formulário e o caso de uso precisam usar **a mesma**
 * implementação (RN-050): validar no formulário e confiar no texto lá dentro é como não
 * validar — a importação, o seed e qualquer caminho futuro passariam por fora.
 *
 * Guarda apenas dígitos. A formatação é assunto de exibição e acontece na saída.
 */
export const CPF_ERROR = {
  INVALID: 'WORKFORCE.CPF_INVALID',
  TAKEN: 'WORKFORCE.CPF_TAKEN',
} as const;

export class Cpf {
  private constructor(private readonly digits: string) {}

  /**
   * Aceita com ou sem máscara.
   *
   * Recusa sequência repetida (`111.111.111-11`) mesmo com os dígitos verificadores
   * batendo: elas passam na conta e não existem como CPF de gente.
   */
  static parse(value: string): Result<Cpf, DomainError> {
    const digits = value.replace(/\D/g, '');

    if (digits.length !== 11) {
      return err(domainError(CPF_ERROR.INVALID, 'O CPF deve ter 11 dígitos.'));
    }
    if (/^(\d)\1{10}$/.test(digits)) {
      return err(domainError(CPF_ERROR.INVALID, 'CPF inválido.'));
    }
    if (!hasValidCheckDigits(digits)) {
      return err(domainError(CPF_ERROR.INVALID, 'CPF inválido — confira os dígitos.'));
    }

    return ok(new Cpf(digits));
  }

  static isValid(value: string): boolean {
    return Cpf.parse(value).ok;
  }

  /** Só dígitos — é o que vai para o banco e para a chave de unicidade (RN-050). */
  toString(): string {
    return this.digits;
  }

  /** `"123.456.789-09"` — para a tela e para relatório. */
  format(): string {
    return `${this.digits.slice(0, 3)}.${this.digits.slice(3, 6)}.${this.digits.slice(6, 9)}-${this.digits.slice(9)}`;
  }

  equals(other: Cpf): boolean {
    return this.digits === other.digits;
  }
}

/**
 * Os dois dígitos verificadores.
 *
 * Cada um é o resto da soma ponderada por 11; resto menor que 2 vira zero. A conta é a
 * mesma para os dois, mudando só o peso inicial e quantos dígitos entram.
 */
function hasValidCheckDigits(digits: string): boolean {
  const checkDigit = (length: number): number => {
    let sum = 0;
    for (let index = 0; index < length; index += 1) {
      sum += Number(digits[index]) * (length + 1 - index);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10]);
}

/** Máscara progressiva para o campo do formulário: `"1234567"` → `"123.456.7"`. */
export function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}
